import React from 'react';
import type { ContractCreationContext } from '../api';

interface Props {
  context?: ContractCreationContext | null;
}

/**
 * Painel da geração (RN-COM-003) na página de detalhe. Vem da trilha de
 * auditoria (`contrato.created` → `context`): mostra de onde o contrato
 * veio, o que a proposta-suprimento trouxe, e quais divergências ficaram
 * entre o que a proposta tinha e o que foi Digitado.
 */
export function ContractOriginPanel({ context }: Props): React.ReactElement | null {
  if (!context || !context.origem) return null;

  const imported = Object.entries(context.importedFromProposal ?? {});
  const conflicts = context.importConflicts ?? [];
  const deferred = context.deferredFromProposal ?? [];

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Origem</h3>
        {context.origem === 'proposta' ? (
          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200">
            Gerado da proposta {context.proposalCodigo ?? ''}
            {context.proposalVersionNumero ? ` · v${context.proposalVersionNumero}` : ''}
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-200">
            Contrato manual
          </span>
        )}
      </div>

      {imported.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-600">Importado da proposta</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {imported.map(([field, value]) => (
              <span
                key={field}
                className="inline-flex items-center rounded bg-white px-1.5 py-0.5 text-[11px] text-gray-700 ring-1 ring-gray-200"
                title={`${field}: ${String(value)}`}
              >
                <span className="font-medium">{field}</span>
                <span className="ml-1 text-gray-500 truncate max-w-[14rem]">{String(value)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {conflicts.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-600">
            Divergências registradas na criação
          </p>
          <ul className="mt-1 space-y-0.5 text-[11px] text-gray-700">
            {conflicts.map((c) => (
              <li key={c.field} className="flex flex-wrap gap-1">
                <span className="font-medium">{c.field}</span>
                <span className="text-gray-500">
                  proposta {c.proposalValue} · contrato {c.dtoValue}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {deferred.length > 0 && (
        <p className="text-[11px] text-gray-500">
          Da proposta, gravados apenas no retrato da 1ª versão (ainda sem coluna no contrato):{' '}
          {deferred.join(', ')}.
        </p>
      )}
    </div>
  );
}
